import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import { selectMessages } from '@/state/chatStore';
import { useChatState } from '@/ui/ChatProvider';
import { MessageBubble } from './MessageBubble';
import { TypingIndicator } from './TypingIndicator';
import { InactivityNotice } from './InactivityNotice';
import { useAutoScroll } from '@/hooks/useAutoScroll';
import { ChevronDownIcon } from '@/ui/primitives/Icon';
export function Transcript() {
    const messages = useChatState(selectMessages);
    const isTyping = useChatState((s) => s.isAgentTyping);
    const status = useChatState((s) => s.status);
    const inactivity = useChatState((s) => s.inactivity);
    // Streaming appends to the last message's `content`, so include it in the
    // scroll deps to stay pinned token by token.
    const lastContent = messages.at(-1)?.content ?? '';
    const { ref, onScroll, scrollToBottom, showJumpButton } = useAutoScroll([
        messages.length,
        lastContent,
        isTyping,
        // The notice appears under the last reply; scroll it into view like one.
        inactivity,
    ]);
    const rows = useMemo(() => messages.map((message, index) => ({
        message,
        // Only the first message of a run carries an avatar (and its author
        // label). A run ends when the writer changes: the AI's last reply and a
        // person's first are two runs, though both are "assistant".
        showAvatar: messages[index - 1]?.role !== message.role ||
            authorKey(messages[index - 1]) !== authorKey(message),
    })), [messages]);
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "ac-transcript", ref: ref, onScroll: onScroll, role: "log", "aria-live": "polite", "aria-busy": isTyping, children: [status === 'connecting' && !messages.length && _jsx(TranscriptSkeleton, {}), rows.map(({ message, showAvatar }) => (_jsx(MessageBubble, { message: message, showAvatar: showAvatar }, message.id))), isTyping && _jsx(TypingIndicator, {}), _jsx(InactivityNotice, {})] }), showJumpButton && (_jsxs("button", { type: "button", className: "ac-jump", onClick: () => scrollToBottom(), children: [_jsx(ChevronDownIcon, {}), "Latest"] }))] }));
}
function TranscriptSkeleton() {
    return (_jsxs("div", { className: "ac-skeleton", "aria-hidden": "true", children: [_jsx("div", { className: "ac-skeleton-line", style: { width: '55%' } }), _jsx("div", { className: "ac-skeleton-line", style: { width: '80%' } }), _jsx("div", { className: "ac-skeleton-line", style: { width: '40%' } })] }));
}
/** Who wrote a message, for grouping: a person by id, the AI as one writer. */
function authorKey(message) {
    const author = message?.author;
    return author?.kind === 'human' ? `human:${author.id}` : 'ai';
}
